import React from 'react';
import classnames from 'classnames';
import { getBackgroundColorClassName } from 'src/components/Header/utils/Header';
import { NavBackgroundColor } from 'src/types/global-types';
import { LazyImg } from 'src/hooks/useLazyLoad';
import { useQuery } from '@apollo/client';
import getWrapperWidgets from 'src/queries/getWrapperWidgets.graphql';

import classes from './Wrapper.scss';

type WrapperProps = {
    className?: string;
    variant?:
        | 'normal'
        | 'page-inner'
        | 'page-product'
        | 'widget-wrapper'
        | 'widget-wrapper-padded'
        | 'widget-wrapper-no-padding';
    element?: 'div' | 'nav' | 'header' | 'footer' | 'section';
    id?: string;
    lighterBackground?: boolean;
    overflowHidden?: boolean;
} & NavBackgroundColor;

export const Wrapper: React.FC<React.PropsWithChildren<WrapperProps>> = ({
    bg,
    children,
    className,
    variant = 'normal',
    element = 'div',
    lighterBackground = false,
    overflowHidden = false,
    ...restProps
}) => {
    const isWrapperWidget =
        variant === 'widget-wrapper' || variant === 'widget-wrapper-padded' || variant === 'widget-wrapper-no-padding';

    const { data } = useQuery(getWrapperWidgets, {
        skip: !isWrapperWidget,
    });

    const widgetData = data?.sideBgWidget;
    const leftWidgetData =
        widgetData?.find(
            (el) => el.title.toLowerCase().includes('left') && Boolean(el.widget_parameters?.status) === true,
        ) || null;
    const rightWidgetData =
        widgetData?.find(
            (el) => el.title.toLowerCase().includes('right') && Boolean(el.widget_parameters?.status) === true,
        ) || null;

    const withWrapper = isWrapperWidget && (leftWidgetData || rightWidgetData);

    const classesString = classnames(classes.root, className || '', {
        [classes.variantPageInner]: variant === 'page-inner',
        [classes.variantPageProduct]: variant === 'page-product',
        [classes.lighterBackground]: lighterBackground,
        [classes.overflowHidden]: overflowHidden,
        [classes.variantWidgetWrapper]:
            variant === 'widget-wrapper' ||
            variant === 'widget-wrapper-padded' ||
            variant === 'widget-wrapper-no-padding',
        [classes.variantWidgetWrapperPadded]: variant === 'widget-wrapper-padded',
        [classes.variantWidgetWrapperNoPadding]: variant === 'widget-wrapper-no-padding',
        ...getBackgroundColorClassName(classes, bg),
    });

    if (element === 'nav') {
        return (
            <nav className={classesString} {...restProps}>
                {children}
            </nav>
        );
    }
    if (element === 'header') {
        return (
            <header className={classesString} {...restProps}>
                {children}
            </header>
        );
    }
    if (element === 'footer') {
        return (
            <footer className={classesString} {...restProps}>
                {children}
            </footer>
        );
    }

    return (
        <div className={classnames({ [classes.widgetWrapperContainer]: withWrapper })}>
            {withWrapper && (
                <div
                    className={classes.wrapperWidgetLeft}
                    style={{
                        backgroundColor:
                            leftWidgetData?.widget_parameters?.color || rightWidgetData?.widget_parameters?.color,
                    }}
                >
                    {leftWidgetData && leftWidgetData?.widget_parameters?.image && (
                        <a href={leftWidgetData?.widget_parameters?.link}>
                            <LazyImg
                                className={classes.widgetImage}
                                src={leftWidgetData?.widget_parameters?.image}
                                alt={leftWidgetData?.title || 'Promotional banner'}
                            />
                        </a>
                    )}
                </div>
            )}
            <div className={classnames(classesString, { [classes.centerContent]: withWrapper })} {...restProps}>
                {children}
            </div>
            {withWrapper && (
                <div
                    className={classes.wrapperWidgetRight}
                    style={{
                        backgroundColor:
                            leftWidgetData?.widget_parameters?.color || rightWidgetData?.widget_parameters?.color,
                    }}
                >
                    {rightWidgetData && rightWidgetData?.widget_parameters?.image && (
                        <a href={rightWidgetData?.widget_parameters?.link}>
                            <LazyImg
                                className={classes.widgetImage}
                                src={rightWidgetData?.widget_parameters?.image}
                                alt={rightWidgetData?.title || 'Promotional banner'}
                            />
                        </a>
                    )}
                </div>
            )}
        </div>
    );
};
